import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import QRCode from 'qrcodejs2';

const MARGIN_CELLS = 4;

const QRGenerator = () => {
    const { t } = useTranslation(['qrGenerator']);
    const [text, setText] = useState('https://example.com');
    const [qrSize, setQrSize] = useState(256);
    const [fgColor, setFgColor] = useState('#000000');
    const [bgColor, setBgColor] = useState('#FFFFFF');
    const [inverted, setInverted] = useState(false);
    const qrContainerRef = useRef(null);
    const qrInstance = useRef(null);

    useEffect(() => {
        if (qrContainerRef.current) {
            qrContainerRef.current.innerHTML = '';

            qrInstance.current = new QRCode(qrContainerRef.current, {
                text: text || ' ',
                width: qrSize,
                height: qrSize,
                colorDark: inverted ? bgColor : fgColor,
                colorLight: inverted ? fgColor : bgColor,
                correctLevel: QRCode.CorrectLevel.H
            });
        }

        return () => {
            if (qrContainerRef.current) {
                // eslint-disable-next-line react-hooks/exhaustive-deps
                qrContainerRef.current.innerHTML = '';
            }
        };
    }, [text, qrSize, fgColor, bgColor, inverted]);

    const generateSVG = () => {
        const matrix = qrInstance.current._oQRCode.modules;
        const matrixSize = matrix.length;

        const totalSize = qrSize * (matrixSize + 2 * MARGIN_CELLS) / matrixSize;
        const cellSize = totalSize / (matrixSize + 2 * MARGIN_CELLS);
        const margin = cellSize * MARGIN_CELLS;

        let svgPath = '';

        for (let row = 0; row < matrix.length; row++) {
            for (let col = 0; col < matrix[row].length; col++) {
                if (matrix[row][col]) {
                    const x = margin + col * cellSize;
                    const y = margin + row * cellSize;
                    svgPath += `M ${x},${y} h ${cellSize} v ${cellSize} h -${cellSize} z `;
                }
            }
        }

        const actualFgColor = inverted ? bgColor : fgColor;
        const actualBgColor = inverted ? fgColor : bgColor;

        const svg = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE svg PUBLIC "-//W3C//DTD SVG 1.1//EN" "http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd">
<svg xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 ${totalSize} ${totalSize}" stroke="none">
  <rect width="100%" height="100%" fill="${actualBgColor}"/>
  <path d="${svgPath}" fill="${actualFgColor}"/>
</svg>`;

        return svg;
    };

    const downloadQR = (format) => {
        try {
            if (format === 'svg') {
                const svg = generateSVG();
                const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'qr-code.svg';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            } else if (format === 'png') {
                const originalCanvas = qrContainerRef.current.querySelector('canvas');
                if (!originalCanvas) return;

                const newCanvas = document.createElement('canvas');
                const matrix = qrInstance.current._oQRCode.modules;
                const matrixSize = matrix.length;

                const totalSize = qrSize * (matrixSize + 2 * MARGIN_CELLS) / matrixSize;
                const margin = totalSize * MARGIN_CELLS / (matrixSize + 2 * MARGIN_CELLS);

                newCanvas.width = totalSize;
                newCanvas.height = totalSize;

                const ctx = newCanvas.getContext('2d');

                ctx.fillStyle = inverted ? fgColor : bgColor;
                ctx.fillRect(0, 0, totalSize, totalSize);

                ctx.drawImage(originalCanvas, margin, margin);

                const url = newCanvas.toDataURL('image/png');
                const link = document.createElement('a');
                link.href = url;
                link.download = 'qr-code.png';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (err) {
            console.error('Download error:', err);
        }
    };

    return (
        <div className="bg-gray-50 dark:bg-gray-900 p-4 md:p-8">
            <div className="max-w-2xl mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-lg">
                <div className="p-6 md:p-8">
                    <h1 className="text-2xl md:text-3xl font-bold text-center text-gray-900 dark:text-white mb-8">
                        {t('title')}
                    </h1>

                    <div className="space-y-6 mb-8">
                        <div className="space-y-2">
                            <label htmlFor="qr-text" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                                {t('input.textLabel')}
                            </label>
                            <input
                                id="qr-text"
                                type="text"
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                placeholder={t('input.textPlaceholder')}
                                className="w-full px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                            />
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="qr-size" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                                {t('input.sizeLabel', { qrSize })}
                            </label>
                            <input
                                id="qr-size"
                                type="range"
                                min={128}
                                max={512}
                                step={32}
                                value={qrSize}
                                onChange={(e) => setQrSize(Number(e.target.value))}
                                className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer"
                            />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label htmlFor="fg-color" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                                    {t('input.fgColorLabel')}
                                </label>
                                <input
                                    id="fg-color"
                                    type="text"
                                    value={fgColor}
                                    onChange={(e) => setFgColor(e.target.value)}
                                    placeholder="#000000"
                                    className="w-full px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors text-gray-900 dark:text-white"
                                />
                            </div>
                            <div className="space-y-2">
                                <label htmlFor="bg-color" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                                    {t('input.bgColorLabel')}
                                </label>
                                <input
                                    id="bg-color"
                                    type="text"
                                    value={bgColor}
                                    onChange={(e) => setBgColor(e.target.value)}
                                    placeholder="#FFFFFF"
                                    className="w-full px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors text-gray-900 dark:text-white"
                                />
                            </div>
                        </div>

                        <div className="flex items-center">
                            <input
                                id="invert-colors"
                                type="checkbox"
                                checked={inverted}
                                onChange={(e) => setInverted(e.target.checked)}
                                className="h-4 w-4 text-blue-600 dark:text-blue-500 focus:ring-blue-500 dark:focus:ring-blue-400 border-gray-300 dark:border-gray-600 rounded cursor-pointer"
                            />
                            <label htmlFor="invert-colors" className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-200 cursor-pointer">
                                {t('input.invertLabel')}
                            </label>
                        </div>
                    </div>

                    <div className="flex justify-center mb-8">
                        <div
                            ref={qrContainerRef}
                            className="border border-gray-200 dark:border-gray-600 rounded-lg p-4"
                            style={{ backgroundColor: inverted ? fgColor : bgColor }}
                        />
                    </div>

                    <div className="flex flex-col sm:flex-row justify-center gap-3">
                        <button
                            onClick={() => downloadQR('svg')}
                            className="flex items-center justify-center gap-2 px-6 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-900"
                        >
                            <Download className="h-5 w-5" />
                            <span>{t('buttons.downloadSvg')}</span>
                        </button>
                        <button
                            onClick={() => downloadQR('png')}
                            className="flex items-center justify-center gap-2 px-6 py-2.5 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-900"
                        >
                            <Download className="h-5 w-5" />
                            <span>{t('buttons.downloadPng')}</span>
                        </button>
                    </div>

                    <div className="mt-8 text-center text-sm text-gray-500">
                        {t('description')}
                    </div>
                </div>
            </div>
            <div className="max-w-2xl mx-auto mt-4 text-center text-sm text-gray-500">
                {t('terms', { host: window.location.hostname ?? 'genqr.pl' })}
            </div>
        </div>
    );
};

export default QRGenerator;